import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useEffect } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface Notification {
  id: string;
  type: string;
  priority: 'critical' | 'urgent' | 'high' | 'medium' | 'low';
  title: string;
  message: string;
  entity_type: string;
  entity_id: string;
  project_id: string | null;
  created_at: string;
  is_read: boolean;
  is_dismissed: boolean;
  is_snoozed: boolean;
  snoozed_until: string | null;
}

export interface NotificationListResponse {
  notifications: Notification[];
  unread_count: number;
  has_more: boolean;
  next_cursor: string | null;
}

// Polling interval for background refresh (2 minutes)
const POLL_INTERVAL_MS = 2 * 60 * 1000;

// ============================================================================
// Main Hook
// ============================================================================

export function useNotifications(tab?: 'active' | 'snoozed' | 'history') {
  const queryClient = useQueryClient();

  // Fetch notifications list
  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['notifications', tab || 'active'],
    queryFn: async () => {
      const params = new URLSearchParams({
        tab: tab || 'active',
        limit: '10',
      });
      const result = await api.get(`/api/notifications?${params}`) as NotificationListResponse;
      return result;
    },
    staleTime: 60 * 1000, // Consider fresh for 1 minute
    refetchOnWindowFocus: true, // Refresh when user returns to tab
  });

  // Fetch unread count (separate query for badge + browser tab)
  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const result = await api.get('/api/notifications/unread-count') as { unread_count: number };
      return result;
    },
    staleTime: 60 * 1000,
    refetchInterval: POLL_INTERVAL_MS, // Poll every 2 minutes while app is open
    refetchOnWindowFocus: true,
  });

  const unreadCount = countData?.unread_count || 0;

  // Update browser tab title with unread count
  useEffect(() => {
    const baseTitle = 'BuildGravity';
    document.title = unreadCount > 0
      ? `(${unreadCount}) ${baseTitle}`
      : baseTitle;

    // Cleanup: restore title when component unmounts
    return () => {
      document.title = baseTitle;
    };
  }, [unreadCount]);

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.post(`/api/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.post(`/api/notifications/${notificationId}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Snooze mutation - frontend calculates UTC time
  const snoozeMutation = useMutation({
    mutationFn: async ({ notificationId, until }: { notificationId: string; until: string }) => {
      await api.post(`/api/notifications/${notificationId}/snooze`, { until });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Unsnooze mutation
  const unsnoozeMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.delete(`/api/notifications/${notificationId}/snooze`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all read mutation
  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await api.post('/api/notifications/mark-all-read');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Confirm CA add-on review charge
  const confirmAddonMutation = useMutation({
    mutationFn: async (projectId: string) => {
      return api.post(`/api/projects/${projectId}/ca/confirm-addon`, {}) as Promise<{
        success: boolean;
        billable_item: Record<string, unknown>;
        invoice: Record<string, unknown>;
      }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    notifications: data?.notifications || [],
    unreadCount,
    hasMore: data?.has_more || false,
    isLoading,
    error,
    markAsRead: markAsReadMutation.mutate,
    dismiss: dismissMutation.mutate,
    snooze: snoozeMutation.mutate,
    unsnooze: unsnoozeMutation.mutate,
    markAllRead: markAllReadMutation.mutate,
    confirmAddon: confirmAddonMutation.mutateAsync,
    isConfirmingAddon: confirmAddonMutation.isPending,
  };
}
